import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState, } from "react";
import SignaturePad from "react-signature-pad-wrapper";
import Alert from "react-s-alert";
import { Text } from "../../../text";
import { Button } from "../../../button";
import { isUrl, isDataUrl } from "../../../urlUtils";
import { FieldLabel } from "../FieldLabel";
import { convertImgToBase64URL } from "./utils";
import { getPreSignedUrl } from "../../formHelpers";
import { ButtonLayoutWrapper, DisplayWrapper, Header, PlaceholderMessage, SignatureCanvasContainer, SignatureContainer, } from "./styles";
import { EmptySignatureMessage } from "./EmptySignatureMessage";
export const Signature = ({ value, label, disabled, testId, options, transformUrl, onChange, onError, }) => {
    const isEmpty = !value;
    const [hasError, setHasError] = useState(false);
    const [retryAt, setRetryAt] = useState(new Date().toISOString());
    const [isTouched, setIsTouched] = useState(false);
    const signatureRef = useRef();
    const onChangeRef = useRef(onChange);
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);
    // HACK: we need event listeners just once, but the onChange callback must be always latest, so we abuse ref.
    useEffect(() => {
        if (signatureRef.current) {
            const signaturePad = signatureRef.current.instance;
            const canvasRef = signatureRef.current.canvas;
            signaturePad.addEventListener("beginStroke", () => {
                setIsTouched(true);
            });
            signaturePad.addEventListener("endStroke", () => {
                setIsTouched(false);
                onChangeRef.current(signatureRef.current?.toDataURL() ?? "", signatureRef.current?.toData() ?? []);
            });
            // Moving the mouse out of canvas does not fire endStroke event by default so we force it
            canvasRef.current.addEventListener("mouseleave", () => {
                const hasStroke = signaturePad.toData().length > 0;
                if (hasStroke) {
                    signaturePad.dispatchEvent(new Event("endStroke"));
                }
            });
        }
    }, []);
    useEffect(() => {
        const initializeCanvas = async () => {
            let dataURL;
            if (isDataUrl(value)) {
                dataURL = value;
            }
            else if (isUrl(value)) {
                try {
                    const preSignedUrl = await getPreSignedUrl(value, transformUrl);
                    dataURL = await convertImgToBase64URL(preSignedUrl);
                }
                catch (error) {
                    setHasError(true);
                    const message = "Unable to load signature, please try to reload.";
                    Alert.error(message);
                    if (onError) {
                        onError(error, { value, message });
                    }
                }
            }
            if (dataURL) {
                signatureRef.current?.fromDataURL(dataURL);
            }
        };
        if (signatureRef.current && value !== signatureRef.current.toDataURL()) {
            initializeCanvas();
        }
    }, [value, retryAt, transformUrl, onError]);
    return (_jsxs(SignatureContainer, { disabled: disabled || hasError, children: [_jsxs(Header, { children: [_jsxs(FieldLabel, { disabled: disabled, children: [label, _jsx("input", { type: "text", hidden: true, value: value, disabled: disabled || hasError, onChange: event => 
                                // Input just for controlling the Signature value from test environment
                                onChange(event.currentTarget.value, []) })] }), _jsxs(ButtonLayoutWrapper, { children: [_jsx(DisplayWrapper, { isVisible: hasError, children: _jsx(Button, { outline: true, color: "danger", size: "small", icon: "arrows-cw", onClick: () => setRetryAt(new Date().toISOString()), children: "Reload" }) }), _jsx(DisplayWrapper, { isVisible: !disabled && !hasError && (!isEmpty || isTouched), children: _jsx(Button, { outline: true, color: "danger", size: "small", icon: "close", onClick: () => {
                                        signatureRef.current.clear();
                                        setIsTouched(false);
                                        onChange("", []);
                                    }, children: "Clear" }) })] })] }), _jsxs(SignatureCanvasContainer, { "data-test-id": testId, id: "signature", filled: !isEmpty || isTouched, disabled: disabled || hasError, children: [hasError && (_jsx(PlaceholderMessage, { children: _jsx(Text, { color: "deepOrange100", fontSize: 4, children: "Unable to load signature, please try to reload." }) })), isEmpty && !isTouched && _jsx(EmptySignatureMessage, {}), _jsx(SignaturePad, { ref: signatureRef, redrawOnResize: true, options: options })] })] }));
};
Signature.defaultProps = {
    value: "",
    disabled: false,
    transformUrl: (val) => val,
    options: {
        minWidth: 2,
        maxWidth: 3,
        dotSize: 3,
        velocityFilterWeight: 0.1,
    },
};
